import React, { useRef, useState } from 'react';

import SPAButton from '@/components/admin/SysadminSPA/shared/SPAButton';
import cn from '@/utils/cn';

interface Props {
  // Rendered beside the dropzone, vertically centered on it (e.g. the submit button).
  children?: React.ReactNode;
  file: File | null;
  onFileChange: (file: File | null) => void;
}

// A clean, on-brand file picker: a styled "Choose file" button plus the selected filename,
// with drag-and-drop onto the whole field. Replaces the raw browser file input. The hidden
// input keeps its label association (for accessibility and testing); the button is the
// keyboard-focusable trigger.
const FileField = ({ children, file, onFileChange }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragActive(false);
    const dropped = event.dataTransfer.files?.[0];
    if (dropped) onFileChange(dropped);
  };

  const dropzone = cn(
    'tw-flex tw-items-center tw-gap-3 tw-rounded tw-border tw-border-dashed tw-px-3 tw-py-2 tw-box-border',
    dragActive ? 'tw-border-spa-accent tw-bg-spa-accent-bg-subtle' : 'tw-border-spa-border-input',
  );

  return (
    <div className="tw-flex tw-flex-col">
      <label className="tw-text-sm tw-font-bold tw-text-spa-heading" htmlFor="ptc-idml">Print file (.idml)</label>
      <div className="tw-mt-1 tw-flex tw-items-center tw-gap-4">
        <div
          className={dropzone}
          onDragLeave={() => setDragActive(false)}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDrop={handleDrop}
        >
          <input
            ref={inputRef}
            accept=".idml"
            className="tw-sr-only"
            id="ptc-idml"
            tabIndex={-1}
            type="file"
            onChange={e => onFileChange(e.target.files?.[0] ?? null)}
          />
          <SPAButton size="xs" variant="outline" onClick={() => inputRef.current?.click()}>
            {file ? 'Replace file' : 'Choose file'}
          </SPAButton>
          <span className={cn('tw-text-sm', file ? 'tw-text-spa-heading' : 'tw-text-spa-secondary')}>
            {file ? file.name : 'or drag it here'}
          </span>
        </div>
        {children}
      </div>
    </div>
  );
};

export default FileField;
